import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, MoreVertical, Eye, Edit, Trash2, Send, CheckCircle, Download, Search, X, ChevronDown, ChevronLeft, ChevronRight, DollarSign, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { invoicesApi, clientsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/contexts/PermissionContext'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { formatDate, formatCurrency } from '@/lib/utils'
import type { InvoiceStatus, InvoiceListItem } from '@/types'
import { RecordPaymentDialog } from '@/components/RecordPaymentDialog'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const statusColors: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800',
  OVERDUE: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-500',
  PARTIALLY_PAID: 'bg-yellow-100 text-yellow-800',
}

const ALL_STATUSES = 'ALL_STATUSES'
const ALL_CLIENTS = 'ALL_CLIENTS'

export default function InvoicesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [clientSearchOpen, setClientSearchOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filter form state (what user is typing/selecting)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClientId, setSelectedClientId] = useState<string>(ALL_CLIENTS)
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES)

  // Applied filters state (what is actually used for filtering)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [appliedClientId, setAppliedClientId] = useState<string>(ALL_CLIENTS)
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string>(ALL_STATUSES)

  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<InvoiceListItem | null>(null)

  // PDF download loading state
  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null)

  const openPaymentDialog = (invoice: InvoiceListItem) => {
    setSelectedInvoiceForPayment(invoice)
    setPaymentDialogOpen(true)
  }

  // Fetch clients for dropdown
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientsApi.getAllLegacy(),
  })

  const { data: invoices = [], isLoading, error } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => invoicesApi.getAll({}),
  })

  // Filter invoices based on APPLIED filters (client-side filtering)
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      // Search term filter (invoice number)
      if (appliedSearchTerm) {
        const searchLower = appliedSearchTerm.toLowerCase()
        const matchesNumber = invoice.invoice_number.toLowerCase().includes(searchLower)
        const matchesClient = invoice.client_name.toLowerCase().includes(searchLower)
        if (!matchesNumber && !matchesClient) return false
      }

      // Client filter
      if (appliedClientId !== ALL_CLIENTS && invoice.client_id !== appliedClientId) {
        return false
      }

      // Status filter
      if (appliedStatusFilter !== ALL_STATUSES && invoice.status !== appliedStatusFilter) {
        return false
      }

      return true
    })
  }, [invoices, appliedSearchTerm, appliedClientId, appliedStatusFilter])

  // Pagination calculations
  const totalRecords = filteredInvoices.length
  const totalPages = Math.ceil(totalRecords / pageSize)

  const paginatedInvoices = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredInvoices.slice(startIndex, endIndex)
  }, [filteredInvoices, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Apply filters when Search is clicked
  const applyFilters = () => {
    setAppliedSearchTerm(searchTerm)
    setAppliedClientId(selectedClientId)
    setAppliedStatusFilter(statusFilter)
    setCurrentPage(1)
  }

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InvoiceStatus }) =>
      invoicesApi.updateStatus(id, status),
    onSuccess: (_, variables) => {
      toast.success(`Invoice marked as ${variables.status.toLowerCase()}`)
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update invoice status'
      toast.error(errorMessage)
    },
  })

  const handleMarkAsSent = (invoiceId: string) => {
    updateStatusMutation.mutate({ id: invoiceId, status: 'SENT' })
  }

  const handleMarkAsPaid = (invoiceId: string) => {
    updateStatusMutation.mutate({ id: invoiceId, status: 'PAID' })
  }

  // Get selected client name for display
  const selectedClient = selectedClientId !== ALL_CLIENTS ? clients.find(c => c.client_id === selectedClientId) : null

  // Check if any filters are applied (based on applied filters)
  const hasActiveFilters = appliedSearchTerm || appliedClientId !== ALL_CLIENTS || appliedStatusFilter !== ALL_STATUSES

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedClientId(ALL_CLIENTS)
    setStatusFilter(ALL_STATUSES)
    setAppliedSearchTerm('')
    setAppliedClientId(ALL_CLIENTS)
    setAppliedStatusFilter(ALL_STATUSES)
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('invoices.loadingInvoices')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('invoices.failedToLoad')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('invoices.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('invoices.manageInvoices')}
          </p>
        </div>
        {canAddEdit('INVOICES') && (
          <Link to="/billing/invoices/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('invoices.createInvoice')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-[300px]">
          <Input
            placeholder={t('invoices.searchInvoice')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
        </div>

        {/* Client */}
        <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={clientSearchOpen}
              className="w-[180px] justify-between font-normal"
            >
              {selectedClient ? selectedClient.client_name : t('billing.allClients')}
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('billing.searchClients')} />
              <CommandList>
                <CommandEmpty>{t('billing.noClientFound')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="__all__"
                    onSelect={() => {
                      setSelectedClientId(ALL_CLIENTS)
                      setClientSearchOpen(false)
                    }}
                  >
                    {t('billing.allClients')}
                  </CommandItem>
                  {clients.map((client) => (
                    <CommandItem
                      key={client.client_id}
                      value={client.client_name}
                      onSelect={() => {
                        setSelectedClientId(client.client_id)
                        setClientSearchOpen(false)
                      }}
                    >
                      {client.client_name}
                      {client.client_email && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {client.client_email}
                        </span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Status */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('billing.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('billing.allStatuses')}</SelectItem>
            <SelectItem value="DRAFT">{t('billing.draft')}</SelectItem>
            <SelectItem value="SENT">{t('billing.sent')}</SelectItem>
            <SelectItem value="PAID">{t('billing.paid')}</SelectItem>
            <SelectItem value="OVERDUE">{t('billing.overdue')}</SelectItem>
            <SelectItem value="PARTIALLY_PAID">{t('billing.partiallyPaid')}</SelectItem>
            <SelectItem value="CANCELLED">{t('billing.cancelled')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button onClick={applyFilters}>
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}
        </Button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {invoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('invoices.createFirstInvoice')}
            </div>
            {canAddEdit('INVOICES') && (
              <Link to="/billing/invoices/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('invoices.createInvoice')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : filteredInvoices.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('invoices.noMatchingInvoices')}
            </div>
            <Button variant="outline" onClick={clearAllFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('invoices.invoiceNumber')}</TableHead>
                    <TableHead>{t('billing.client')}</TableHead>
                    <TableHead>{t('billing.date')}</TableHead>
                    <TableHead>{t('billing.dueDate')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('billing.amount')}</TableHead>
                    <TableHead className="text-right">{t('invoices.balance')}</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedInvoices.map((invoice) => (
                    <TableRow key={invoice.invoice_id}>
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>{invoice.client_name}</TableCell>
                      <TableCell>{formatDate(invoice.invoice_date)}</TableCell>
                      <TableCell>{formatDate(invoice.due_date)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[invoice.status]} variant="secondary">
                          {t(`billing.${invoice.status.toLowerCase().replace('_', '')}`)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(invoice.total_amount, invoice.currency)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(invoice.balance_due, invoice.currency)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/billing/invoices/${invoice.invoice_id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              {t('invoices.viewInvoice')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={downloadingPdfId === invoice.invoice_id}
                              onClick={async () => {
                                setDownloadingPdfId(invoice.invoice_id)
                                try {
                                  const blob = await invoicesApi.downloadPdf(invoice.invoice_id)
                                  const url = window.URL.createObjectURL(blob)
                                  const a = document.createElement('a')
                                  a.href = url
                                  a.download = `invoice_${invoice.invoice_number}.pdf`
                                  document.body.appendChild(a)
                                  a.click()
                                  window.URL.revokeObjectURL(url)
                                  document.body.removeChild(a)
                                } catch (error: any) {
                                  console.error('Failed to download PDF:', error)
                                  const errorMessage = error.response?.data?.message || error.message || t('invoices.failedToDownloadPdf')
                                  toast.error(errorMessage)
                                } finally {
                                  setDownloadingPdfId(null)
                                }
                              }}>
                              {downloadingPdfId === invoice.invoice_id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="mr-2 h-4 w-4" />
                              )}
                              {t('invoices.downloadPdf')}
                            </DropdownMenuItem>
                            {canAddEdit('INVOICES') && invoice.status === 'DRAFT' && (
                              <>
                                <DropdownMenuItem onClick={() => navigate(`/billing/invoices/${invoice.invoice_id}/edit`)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  {t('invoices.editInvoice')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleMarkAsSent(invoice.invoice_id)}>
                                  <Send className="mr-2 h-4 w-4" />
                                  {t('invoices.markAsSent')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {canAddEdit('INVOICES') && (invoice.status === 'SENT' || invoice.status === 'OVERDUE' || invoice.status === 'PARTIALLY_PAID') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openPaymentDialog(invoice)}>
                                  <DollarSign className="mr-2 h-4 w-4" />
                                  {t('invoices.recordPayment')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {canAddEdit('INVOICES') && (invoice.status === 'SENT' || invoice.status === 'OVERDUE') && (
                              <DropdownMenuItem onClick={() => handleMarkAsPaid(invoice.invoice_id)}>
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t('invoices.markAsPaid')}
                              </DropdownMenuItem>
                            )}
                            {canAddEdit('INVOICES') && invoice.status === 'DRAFT' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem className="text-destructive">
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  {t('invoices.deleteInvoice')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Record Payment Dialog */}
      {selectedInvoiceForPayment && (
        <RecordPaymentDialog
          open={paymentDialogOpen}
          onOpenChange={setPaymentDialogOpen}
          invoiceId={selectedInvoiceForPayment.invoice_id}
          invoiceNumber={selectedInvoiceForPayment.invoice_number}
          balanceDue={selectedInvoiceForPayment.balance_due}
          currency={selectedInvoiceForPayment.currency}
        />
      )}
    </div>
  )
}

